import { CommonModule } from '@angular/common';
import {
  Component,
  NgModule,
  Output,
  Input,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { DxPopupModule, DxTooltipModule } from 'devextreme-angular';
import {
  DxTreeViewModule,
  DxTreeViewComponent,
} from 'devextreme-angular/ui/tree-view';
import * as events from 'devextreme/events';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { PharmacyService } from 'src/app/shared/services/user/pharmacy.service';
import { UserService } from 'src/app/shared/services/user/user.service';
import { OrdersService } from 'src/app/shared/services/order/orders.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { UserProfileModule } from 'src/app/shared/components/user-profile/user-profile.module';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-side-navigation-menu',
  templateUrl: './side-navigation-menu.component.html',
  styleUrls: ['./side-navigation-menu.component.scss'],
})
export class SideNavigationMenuComponent implements AfterViewInit, OnDestroy {
  _compactMode = false;

  @ViewChild(DxTreeViewComponent, { static: true }) menu: DxTreeViewComponent;
  @Output() selectedItemChanged = new EventEmitter<string>();
  @Output() openMenu = new EventEmitter<any>();
  @Output() menuToggle = new EventEmitter<boolean>();
  @Input() items: any[];
  @Input() isMedigiAdmin = false;
  @Input() menuToggleEnabled = false;
  @Input()
  set selectedItem(value: string) {
    if (this.menu.instance) {
      this.menu.instance.selectItem(value);
    }
  }
  @Input()
  get compactMode() {
    return this._compactMode;
  }
  set compactMode(val) {
    this._compactMode = val;
    if (val && this.menu.instance) {
      this.menu.instance.collapseAll();
      this.removeEventListeners();
      this.addEventListeners();
    }
  }

  user: any;
  tooltipContent: string;
  target: any;
  isVisible = false;
  today: Date = new Date();
  subscriptions: Array<Subscription> = [];
  private tooltipListeners: Array<{
    element: HTMLElement;
    enter: EventListenerOrEventListenerObject;
    leave: EventListenerOrEventListenerObject;
  }> = [];
  private tooltipMetadata = new WeakMap<
    HTMLElement,
    { text: string; target: HTMLElement | null }
  >();
  isProfilePopupOpen: boolean = false;
  largeLogo = '';
  smallLogo = '';
  logoAltText = '';
  supplierConfig = environment.supplierConfig;

  constructor(
    private elementRef: ElementRef,
    public sessionService: SessionService,
    public pharmacyService: PharmacyService,
    private router: Router,
    private userService: UserService,
  ) {}

  ngOnInit() {
    this.getNavLogo();
    this.getUserInfo();
  }

  ngAfterViewInit() {
    events.on(this.elementRef.nativeElement, 'dxclick', e => {
      this.openMenu.next(e);
    });
  }

  ngOnDestroy() {
    events.off(this.elementRef.nativeElement, 'dxclick');
    this.removeEventListeners();
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onContentReady(event) {
    this.updateSelection(event);
    this.addEventListeners();
  }

  getUserInfo() {
    this.userService.getMe().subscribe(result => (this.user = result.data));
  }

  updateSelection(event) {
    const nodeClass = 'dx-treeview-node';
    const selectedClass = 'dx-state-selected';
    const leafNodeClass = 'dx-treeview-node-is-leaf';
    const element: HTMLElement = event.element;

    const rootNodes = element.querySelectorAll(
      `.${nodeClass}:not(.${leafNodeClass})`,
    );
    Array.from(rootNodes).forEach(node => {
      node.classList.remove(selectedClass);
    });

    let selectedNode = element.querySelector(`.${nodeClass}.${selectedClass}`);
    while (selectedNode && selectedNode.parentElement) {
      if (selectedNode.classList.contains(nodeClass)) {
        selectedNode.classList.add(selectedClass);
      }

      selectedNode = selectedNode.parentElement;
    }
  }

  onItemClick(event) {
    this.selectedItemChanged.emit(event);
  }

  onMenuInitialized(event) {
    event.component.option('deferRendering', false);
  }

  removeEventListeners() {
    for (const { element, enter, leave } of this.tooltipListeners) {
      element.removeEventListener('mouseenter', enter);
      element.removeEventListener('mouseleave', leave);
      this.tooltipMetadata.delete(element);
    }
    this.tooltipListeners = [];
  }

  addEventListeners() {
    if (!this._compactMode) {
      return;
    }

    this.removeEventListeners();

    const elements: HTMLElement[] = Array.from(
      this.elementRef.nativeElement.querySelectorAll(
        '.dx-treeview-node[aria-level="1"] .dx-treeview-item',
      ) as NodeListOf<HTMLElement>,
    );

    for (const element of elements) {
      const metadata = this.buildTooltipMetadata(
        element,
        this.tooltipMetadata.get(element),
      );
      if (!metadata) {
        this.tooltipMetadata.delete(element);
        continue;
      }

      this.tooltipMetadata.set(element, metadata);
      const enter = () => this.showTooltipForElement(element);
      const leave = () => this.hideTooltip();
      element.addEventListener('mouseenter', enter);
      element.addEventListener('mouseleave', leave);
      this.tooltipListeners.push({ element, enter, leave });
    }
  }

  private showTooltipForElement(element: HTMLElement | null) {
    const metadata = element ? this.tooltipMetadata.get(element) : null;
    if (!element || !metadata?.text) {
      this.hideTooltip();
      return;
    }

    this.tooltipContent = metadata.text;
    this.target = metadata.target ?? element;
    this.isVisible = true;
  }

  hideTooltip() {
    this.isVisible = false;
    this.target = null;
  }

  private buildTooltipMetadata(
    element: HTMLElement,
    existing?: { text: string; target: HTMLElement | null },
  ) {
    if (
      !element.closest('.dx-treeview-node[aria-level="1"]') ||
      !element.querySelector('.dx-treeview-item-content')
    ) {
      return null;
    }

    const text =
      element.querySelector('.dx-treeview-item-content')?.textContent?.trim() ??
      '';
    if (!text) {
      return null;
    }

    if (
      existing?.target &&
      existing.text === text &&
      this.isEligibleIcon(existing.target)
    ) {
      return existing;
    }

    const target = this.findTooltipAnchor(element, existing?.target ?? null);
    return { text, target };
  }

  private findTooltipAnchor(
    element: HTMLElement,
    preferred: HTMLElement | null,
  ) {
    const iconSelector = '.dx-icon, [class*="dx-icon-"]';
    if (preferred && this.isEligibleIcon(preferred)) {
      return preferred;
    }
    return (
      Array.from(element.querySelectorAll<HTMLElement>(iconSelector)).find(
        icon => this.isEligibleIcon(icon),
      ) ?? null
    );
  }

  private isEligibleIcon(element: HTMLElement) {
    if (element.closest('.dx-treeview-toggle-item-visibility')) {
      return false;
    }

    return window.getComputedStyle(element).display !== 'none';
  }

  openUserProfile() {
    this.isProfilePopupOpen = true;
  }

  closingPopup() {
    this.isProfilePopupOpen = false;
  }

  toggleMenu = () => {
    this.menuToggle.emit();
  };

  getNavLogo() {
    if (!environment.hydraEnabled || !this.supplierConfig.logo) {
      this.setDefaultLogos();
    } else {
      try {
        this.smallLogo = this.supplierConfig.logo.small;
        this.largeLogo = this.supplierConfig.logo.large;
        this.logoAltText = this.supplierConfig.logo.alt;
      } catch {
        this.setDefaultLogos();
      }
    }
  }

  setDefaultLogos() {
    this.largeLogo = '/assets/img/logos/medigi-logo.png';
    this.smallLogo = '/assets/img/logos/medigi-blue-icon.svg';
    this.logoAltText = 'Medigi Logo';
  }
}

@NgModule({
  imports: [
    CommonModule,
    BrowserModule,
    DxTreeViewModule,
    DxTooltipModule,
    UserProfileModule,
    DxPopupModule,
  ],
  declarations: [SideNavigationMenuComponent],
  exports: [SideNavigationMenuComponent],
})
export class SideNavigationMenuModule {}
